import { createSlice } from '@reduxjs/toolkit';
import { TResponsiveTableRow } from '../../components/common/generic/responsiveTable/types';
import selectionGetInitialState from '../selection/getInitialState';
import getReduxActions from '../selection/getReduxActions';
import { shoutKeyhandlerLive } from '../selection/makeKeyHandler';
import setNewFocused from '../selection/setNewFocused';
import {
  TResponsiveTableAction,
  TResponsiveTableIndexColumns,
  TResponsiveTableSlice,
  TResponsiveTableState,
} from './types';
import { TSelectionStatesColumns } from '../selection/types';
import { icons } from '../../components/common/Icon';

function getInitialState(): TResponsiveTableState {
  const newState: TResponsiveTableState = {
    ...selectionGetInitialState(),
    allowSelection: true,
    allowSorting: true,
    filters: [],
    isLoading: false,
    isMultiple: true,
    accordionIndexColumns: [] as TResponsiveTableIndexColumns,
    expandedRows: [],
  };

  return newState;
}

const responsiveTableSlice = createSlice({
  name: 'responsiveTableSlice',
  initialState: {} as TResponsiveTableSlice,
  reducers: {
    update(
      state,
      {
        payload: { tableName, ...rest },
      }: TResponsiveTableAction<
        Partial<TResponsiveTableState> & { isFocused?: boolean }
      >,
    ) {
      if (!state[tableName]) state[tableName] = getInitialState();
      state[tableName] = { ...state[tableName], ...rest };
      state[tableName].isFiltersRowOpen =
        state[tableName].isFiltersRowOpen ||
        !!state[tableName].filters.find(
          (current) =>
            current.required && current.column && !current.asAdditional,
        );

      /**
       * Cuando se setean valores a los filtros y la fila de filtros estaba
       * cerrada, la fila se de filtros se abre.
       */
      const areColumnFiltersWithValue = rest.filters?.some(
        (filter) =>
          filter.column &&
          !filter.asAdditional &&
          filter.currentValue !== '' &&
          filter.currentValue !== undefined &&
          !filter.hide,
      );
      if (areColumnFiltersWithValue && !state[tableName].isFiltersRowOpen)
        state[tableName].isFiltersRowOpen = true;

      setNewFocused(state[tableName], {
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        shouldSelectNewFocused: rest.isFocused || state[tableName].isFocused,
      });

      if (rest.columns) {
        state[tableName].nonAdditionalColumnsCount = rest.columns.filter(
          (current) => !current.showAsAdditional,
        ).length;
      }
      /* 
      iconColor: current.iconColor ?? 'palette.primary.contrastText',
 */
      if (rest.rows) {
        const maxStatesRow = Math.max(
          ...(state[tableName]?.rows?.map(
            (current) => current.states?.length ?? 0,
          ) ?? [0]),
        );
        const reservedStatesCount = state[tableName].reserveColumnsForStates;

        state[tableName].statesColumns = new Array(
          Math.max(maxStatesRow, reservedStatesCount?.length ?? 0),
        )
          .fill(1)
          .map((_, i) => {
            const newStateColumn: TSelectionStatesColumns = {
              Icon: reservedStatesCount?.[i]?.Icon ?? icons.Info,
              iconColor:
                reservedStatesCount?.[i]?.iconColor ??
                'palette.primary.contrastText',
              tooltip:
                reservedStatesCount?.[i]?.tooltip ??
                'No reservaste estado para esta columna, papi',
            };

            return newStateColumn;
          });
      }

      if (rest.filters) {
        state[tableName].hasNonAdditionalFilters =
          rest.filters.filter(
            (current) =>
              !current.asAdditional &&
              (rest.columns ?? state[tableName].columns)?.find(
                (search) => search.name === current.column,
              ),
          ).length > 0;
      }
    },
    addRows(
      state,
      { payload }: TResponsiveTableAction<{ newRows: TResponsiveTableRow[] }>,
    ) {
      if (!state[payload.tableName])
        state[payload.tableName] = getInitialState();
      state[payload.tableName].rows = state[payload.tableName].rows.concat(
        ...payload.newRows,
      );
      setNewFocused(state[payload.tableName], {
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        shouldSelectNewFocused: true,
        newFocusedRow: state[payload.tableName].rows.length,
      });
    },
    deleteRows(
      state,
      { payload }: TResponsiveTableAction<{ rowIndices: number[] }>,
    ) {
      const table = state[payload.tableName];
      const minIndex = payload.rowIndices.reduce((min, current) => {
        return current < min ? current : min;
      }, Infinity);
      table.rows = table.rows.filter(
        (_, index) => !payload.rowIndices.includes(index),
      );

      setNewFocused(state[payload.tableName], {
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        shouldSelectNewFocused: true,
        newFocusedRow: minIndex,
      });
      state[payload.tableName].isFocused = false;
    },
    destroy(state, { payload: { tableName } }: TResponsiveTableAction) {
      delete state[tableName];
    },
    swapFocusedRow(
      state,
      {
        payload: { newIndex, tableName },
      }: TResponsiveTableAction<{ newIndex: number }>,
    ) {
      /**
       * Esta función se usa básicamente para el orden de las filas
       * por teclado.
       */
      const tableState = state[tableName];
      const focusedIndex = tableState.focusedRow;

      if (
        focusedIndex === undefined ||
        newIndex < 0 ||
        newIndex >= tableState.rows.length
      )
        return;

      if (focusedIndex === newIndex) {
        if (tableState.draggingRow === newIndex)
          tableState.draggingRow = undefined;
        else tableState.draggingRow = newIndex;
        return;
      }

      shoutKeyhandlerLive('whichIsTheNewIndex', { newIndex });

      const isSelected = tableState.selectedRows.includes(focusedIndex);

      if (isSelected) {
        tableState.selectedRows = [
          ...tableState.selectedRows.filter(
            (current) => current !== focusedIndex,
          ),
          newIndex,
        ];
      }

      const aux = tableState.rows[newIndex];
      tableState.rows[newIndex] = tableState.rows[focusedIndex];
      tableState.rows[focusedIndex] = aux;

      tableState.focusedRow = newIndex;
      tableState.draggingRow = newIndex;
    },
    toggleFiltersRow(
      state,
      { payload }: TResponsiveTableAction<{ isOpen?: boolean }>,
    ) {
      state[payload.tableName].isFiltersRowOpen =
        payload.isOpen ?? !state[payload.tableName].isFiltersRowOpen;
    },

    // Rows selection
    ...getReduxActions(),
  },
});

export const responsiveTableActions = responsiveTableSlice.actions;
export default responsiveTableSlice.reducer;
